import React from 'react';
import { Chart } from 'primereact/chart';

type MonthlyData = {
    month: string;
    value: number;
};

type RevenueChartProps = {
    data: MonthlyData[];
    totalRevenue: string;
    yearlyGrowth: string;
    avgMonthly?: string;
};

const RevenueChart: React.FC<RevenueChartProps> = ({ data, totalRevenue, yearlyGrowth, avgMonthly = '$68,250' }) => {
    const chartData = {
        labels: data.map((item) => item.month),
        datasets: [
            {
                label: 'Revenue',
                data: data.map((item) => item.value),
                fill: false,
                backgroundColor: 'rgba(0, 120, 180, 0.1)',
                borderColor: '#0078B4', // Blue line matching the image
                tension: 0.4,
                pointRadius: 6,
                pointBackgroundColor: '#FFFFFF',
                pointBorderColor: '#0078B4',
                pointBorderWidth: 2,
                borderWidth: 2.5,
            },
        ],
    };

    const options = {
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                backgroundColor: '#FFFFFF',
                titleColor: '#333333',
                bodyColor: '#333333',
                borderColor: '#DDDDDD',
                borderWidth: 1,
                padding: 10,
                displayColors: false,
                callbacks: {
                    title: function (tooltipItem: any) {
                        return tooltipItem[0].label;
                    },
                    label: function (context: any) {
                        return `$${context.parsed.y.toLocaleString()}`;
                    },
                },
                bodyFont: {
                    weight: 'bold',
                },
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                grid: {
                    color: 'rgba(200, 200, 200, 0.2)',
                    drawBorder: false,
                },
                ticks: {
                    callback: function (value: any) {
                        // Format like the image shows (e.g., 25,000 as 25,000 not $25,000)
                        return value.toLocaleString();
                    },
                    color: '#666666',
                    font: {
                        size: 11,
                    },
                },
                min: 0,
                max: 100000, // Based on the image showing scale up to 100,000
                stepSize: 25000,
            },
            x: {
                grid: {
                    display: false,
                    drawBorder: false,
                },
                ticks: {
                    color: '#666666',
                    font: {
                        size: 11,
                    },
                },
            },
        },
        maintainAspectRatio: false,
        responsive: true,
        elements: {
            line: {
                tension: 0.4,
            },
        },
    };

    return (
        <div className="revenue-chart-card dashboard-card-wrapper coming-soon-card">
            <div className="revenue-chart-header">
                <h2 className="chart-title">Monthly Revenue</h2>
                <p className="chart-subtitle">Revenue trends over the year</p>
            </div>
            <div className="chart-container">
                <Chart type="line" data={chartData} options={options} />
            </div>
            <div className="chart-stats">
                <div className="stat-box">
                    <span className="stat-value">{totalRevenue}</span>
                    <span className="stat-label">Annual Revenue</span>
                </div>
                <div className="stat-box">
                    <span className="stat-value growth">{yearlyGrowth}</span>
                    <span className="stat-label">Monthly Growth</span>
                </div>
                <div className="stat-box">
                    <span className="stat-value">{avgMonthly}</span>
                    <span className="stat-label">Avg Monthly</span>
                </div>
            </div>
        </div>
    );
};

export default RevenueChart;
